import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  X,
  Link2,
  Send,
  Copy,
  Check,
  RefreshCw,
  Ban,
  Loader2,
  Smartphone,
  MessageCircle,
  Mail,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
} from 'lucide-react';
import {
  createPaymentLink,
  listBookingPaymentLinks,
  getPaymentLink,
  resendPaymentLink,
  cancelPaymentLink,
  isLinkActive,
} from '../../services/paymentLinkService.js';

const CHANNELS = [
  { key: 'SMS', label: 'SMS', icon: Smartphone, needs: 'phone' },
  { key: 'WHATSAPP', label: 'WhatsApp', icon: MessageCircle, needs: 'phone' },
  { key: 'EMAIL', label: 'Email', icon: Mail, needs: 'email' },
];

const EXPIRY_OPTIONS = [
  { value: 60, label: '1 hour' },
  { value: 360, label: '6 hours' },
  { value: 1440, label: '24 hours' },
  { value: 4320, label: '3 days' },
  { value: 10080, label: '7 days' },
];

const STATUS_STYLE = {
  CREATED: 'bg-blue-50 text-blue-700 border-blue-200',
  PARTIALLY_PAID: 'bg-amber-50 text-amber-700 border-amber-200',
  PAID: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  EXPIRED: 'bg-slate-100 text-slate-600 border-slate-200',
  CANCELLED: 'bg-rose-50 text-rose-700 border-rose-200',
};

const STATUS_LABEL = {
  CREATED: 'Awaiting payment',
  PARTIALLY_PAID: 'Partially paid',
  PAID: 'Paid',
  EXPIRED: 'Expired',
  CANCELLED: 'Cancelled',
};

const POLL_MS = 10000;
const RESEND_COOLDOWN_MS = 60000; // mirrors the backend limit

const formatRupees = (n) =>
  `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
    : '—';

const timeLeft = (iso, now) => {
  const ms = new Date(iso).getTime() - now;
  if (ms <= 0) return 'expired';
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m left`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 48) return `${hrs}h ${mins % 60}m left`;
  return `${Math.floor(hrs / 24)}d left`;
};

const deliveryReason = (result) => {
  if (!result || result.success) return '';
  if (result.reason === 'not_configured') return 'not configured';
  if (result.reason === 'no_phone') return 'no phone number';
  if (result.reason === 'no_email') return 'no email';
  return 'failed';
};

const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold border ${
      STATUS_STYLE[status] || STATUS_STYLE.EXPIRED
    }`}
  >
    {STATUS_LABEL[status] || status}
  </span>
);

const DeliveryChips = ({ delivery }) => {
  const entries = Object.entries(delivery || {});
  if (!entries.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {entries.map(([channel, result]) => {
        const ok = result?.success;
        const meta = CHANNELS.find((c) => c.key === channel);
        const reason = deliveryReason(result);
        return (
          <span
            key={channel}
            title={ok ? `Sent ${formatDateTime(result.at)}` : `${meta?.label || channel}: ${reason}`}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
              ok
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-rose-50 text-rose-700 border-rose-200'
            }`}
          >
            {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
            {meta?.label || channel}
            {!ok && reason ? ` · ${reason}` : ''}
          </span>
        );
      })}
    </div>
  );
};

/**
 * Send / track a Razorpay payment link for a booking.
 *
 * @param {Object}   props.trip     - command-center trip ({ id, stage, customer, fare, rawBooking })
 * @param {Function} props.onClose
 * @param {Function} [props.onPaid] - called once when a link is seen to flip to PAID
 */
const PaymentLinkModal = ({ trip, onClose, onPaid }) => {
  const raw = trip?.rawBooking || {};
  const customer = trip?.customer || {};
  const fare = trip?.fare || {};
  const bookingId = trip?.id;

  const isClosedStage = ['Cancelled', 'Rejected'].includes(trip?.stage);
  const defaultFare = fare.finalFare || raw.final_fare || fare.estimatedFare || raw.estimated_fare || '';

  const initialPhone = useMemo(() => {
    const phone = customer.phone && customer.phone !== '—' ? String(customer.phone) : '';
    if (!phone) return '';
    return phone.startsWith('+') || !customer.phoneCountryCode ? phone : `${customer.phoneCountryCode}${phone}`;
  }, [customer.phone, customer.phoneCountryCode]);
  const initialEmail = customer.email || '';

  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [amount, setAmount] = useState(isClosedStage ? '' : String(defaultFare || ''));
  const [phone, setPhone] = useState(initialPhone);
  const [email, setEmail] = useState(initialEmail);
  const [expiryMinutes, setExpiryMinutes] = useState(1440);
  const [channels, setChannels] = useState(() => ({
    SMS: !!initialPhone,
    WHATSAPP: !!initialPhone,
    EMAIL: !!initialEmail,
  }));
  const [submitting, setSubmitting] = useState(false);
  const [busyLinkId, setBusyLinkId] = useState(null);
  const [formError, setFormError] = useState('');
  const [notice, setNotice] = useState(null); // { type: 'success' | 'warning', text }
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(Date.now());

  const paidReported = useRef(false);
  const sawActiveLink = useRef(false);

  const activeLink = links.find(isLinkActive) || null;
  const paidLink = links.find((l) => l.status === 'PAID') || null;
  const alreadyPaid = !!paidLink || (raw.payment_status || fare.paymentStatus) === 'PAID';

  const loadLinks = useCallback(async () => {
    try {
      const { paymentLinks } = await listBookingPaymentLinks(bookingId);
      setLinks(paymentLinks || []);
      setLoadError('');
      return paymentLinks || [];
    } catch (err) {
      setLoadError(err.message || 'Could not load payment links');
      return null;
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  // Keep status fresh while a link is waiting to be paid (webhook updates the DB)
  useEffect(() => {
    if (!activeLink) return undefined;
    const timer = setInterval(() => {
      setNow(Date.now());
      loadLinks();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [activeLink, loadLinks]);

  // Report a payment only when we watched an active link flip to PAID (not when opened on an already-paid booking)
  useEffect(() => {
    if (activeLink) sawActiveLink.current = true;
    if (paidLink && sawActiveLink.current && !paidReported.current) {
      paidReported.current = true;
      if (onPaid) onPaid(paidLink);
    }
  }, [activeLink, paidLink, onPaid]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!trip) return null;

  const toggleChannel = (key) => setChannels((c) => ({ ...c, [key]: !c[key] }));

  const validate = () => {
    const selected = CHANNELS.filter((c) => channels[c.key]);
    if (!selected.length) return 'Select at least one channel to send the link on.';
    if (selected.some((c) => c.needs === 'phone') && !phone.trim()) return 'Enter the patient’s phone number for SMS / WhatsApp.';
    if (selected.some((c) => c.needs === 'email') && !email.trim()) return 'Enter the patient’s email address.';
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return 'Enter a valid email address.';
    if (amount !== '') {
      const n = Number(amount);
      if (!Number.isFinite(n) || n < 1 || n > 100000) return 'Amount must be between ₹1 and ₹1,00,000.';
    } else if (!isClosedStage) {
      return 'Enter the amount to collect.';
    }
    return '';
  };

  const describeDelivery = (delivery, verb) => {
    const entries = Object.entries(delivery || {});
    const sent = entries.filter(([, r]) => r.success).map(([c]) => CHANNELS.find((x) => x.key === c)?.label || c);
    const failed = entries.filter(([, r]) => !r.success);
    if (!sent.length) {
      return { type: 'warning', text: `Link ${verb}, but it could not be delivered on any channel. Copy it and share it manually.` };
    }
    return {
      type: failed.length ? 'warning' : 'success',
      text: `Link ${verb} and sent via ${sent.join(', ')}.${failed.length ? ` ${failed.length} channel(s) failed.` : ''}`,
    };
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setNotice(null);
    const problem = validate();
    setFormError(problem);
    if (problem) return;

    const payload = {
      bookingId,
      channels: CHANNELS.filter((c) => channels[c.key]).map((c) => c.key),
      expiryMinutes: Number(expiryMinutes),
      ...(amount !== '' && { amount: Number(amount) }),
      // Only override the contact when staff actually changed it
      ...(phone.trim() && phone.trim() !== initialPhone && { phone: phone.trim() }),
      ...(email.trim() && email.trim() !== initialEmail && { email: email.trim() }),
    };

    setSubmitting(true);
    try {
      const { delivery, reused } = await createPaymentLink(payload);
      setNotice(describeDelivery(delivery, reused ? 're-sent' : 'created'));
      await loadLinks();
    } catch (err) {
      setFormError(err.message || 'Could not create the payment link.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (link) => {
    setNotice(null);
    setBusyLinkId(link.linkId);
    try {
      const { delivery } = await resendPaymentLink(link.linkId, {});
      setNotice(describeDelivery(delivery, 're-sent'));
      await loadLinks();
    } catch (err) {
      setNotice({ type: 'warning', text: err.message || 'Could not re-send the link.' });
    } finally {
      setBusyLinkId(null);
    }
  };

  const handleCancel = async (link) => {
    if (!window.confirm('Cancel this payment link? The patient will no longer be able to pay with it.')) return;
    setNotice(null);
    setBusyLinkId(link.linkId);
    try {
      await cancelPaymentLink(link.linkId);
      setNotice({ type: 'success', text: 'Payment link cancelled.' });
      await loadLinks();
    } catch (err) {
      setNotice({ type: 'warning', text: err.message || 'Could not cancel the link.' });
    } finally {
      setBusyLinkId(null);
    }
  };

  const handleRefresh = async (link) => {
    setNotice(null);
    setBusyLinkId(link.linkId);
    try {
      await getPaymentLink(link.linkId, true);
      await loadLinks();
      setNotice({ type: 'success', text: 'Status refreshed from Razorpay.' });
    } catch (err) {
      setNotice({ type: 'warning', text: err.message || 'Could not refresh the status.' });
    } finally {
      setBusyLinkId(null);
    }
  };

  const handleCopy = (url) => {
    navigator.clipboard?.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const cooldownLeftSec = (link) => {
    if (!link.lastSentAt) return 0;
    return Math.max(0, Math.ceil((RESEND_COOLDOWN_MS - (now - new Date(link.lastSentAt).getTime())) / 1000));
  };

  const amountChanged = activeLink && amount !== '' && Number(amount) !== Number(activeLink.amount);
  const submitLabel = !activeLink ? 'Send payment link' : amountChanged ? 'Replace link & send' : 'Re-send link';
  const pastLinks = links.filter((l) => l.linkId !== activeLink?.linkId && l.linkId !== paidLink?.linkId);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-5 bg-slate-900/60 overflow-y-auto"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-link-title"
        className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden my-auto text-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
              <Link2 className="w-4 h-4" />
            </span>
            <div className="min-w-0">
              <h2 id="payment-link-title" className="text-base font-bold text-slate-900 leading-tight">
                Payment link
              </h2>
              <p className="text-xs text-slate-500 truncate">
                Booking #{bookingId}
                {customer.name ? ` · ${customer.name}` : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto space-y-4">
          {notice && (
            <div
              role="status"
              className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-xs font-medium ${
                notice.type === 'success'
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                  : 'bg-amber-50 border-amber-200 text-amber-800'
              }`}
            >
              {notice.type === 'success' ? (
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-px" />
              ) : (
                <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
              )}
              <span>{notice.text}</span>
            </div>
          )}

          {loadError && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              {loadError}
            </div>
          )}

          {loading && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading payment links…
            </div>
          )}

          {/* Paid */}
          {alreadyPaid && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start gap-2.5">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
              <div className="text-sm">
                <p className="font-bold text-emerald-800">Payment received</p>
                {paidLink ? (
                  <p className="text-xs text-emerald-700 mt-0.5">
                    {formatRupees(paidLink.amount)} paid {formatDateTime(paidLink.paidAt)}
                    {paidLink.razorpayPaymentId ? ` · ${paidLink.razorpayPaymentId}` : ''}
                  </p>
                ) : (
                  <p className="text-xs text-emerald-700 mt-0.5">This booking is already marked as paid.</p>
                )}
              </div>
            </div>
          )}

          {/* Active link */}
          {!alreadyPaid && activeLink && (
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3.5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <StatusBadge status={activeLink.status} />
                  <span className="text-sm font-bold text-slate-900">{formatRupees(activeLink.amount)}</span>
                </div>
                <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 font-medium">
                  <Clock className="w-3 h-3" />
                  {timeLeft(activeLink.expiresAt, now)}
                </span>
              </div>

              <div className="mt-2.5 flex items-center gap-2">
                <input
                  readOnly
                  value={activeLink.shortUrl}
                  aria-label="Payment link URL"
                  onFocus={(e) => e.target.select()}
                  className="flex-1 min-w-0 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs text-slate-700 font-mono"
                />
                <button
                  onClick={() => handleCopy(activeLink.shortUrl)}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-xs font-semibold text-slate-700 cursor-pointer"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>

              <DeliveryChips delivery={activeLink.delivery} />
              <p className="text-[11px] text-slate-400 mt-1.5">
                Sent {activeLink.sendCount}× · last {formatDateTime(activeLink.lastSentAt)}
              </p>

              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  onClick={() => handleResend(activeLink)}
                  disabled={busyLinkId === activeLink.linkId || cooldownLeftSec(activeLink) > 0}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-xs font-semibold cursor-pointer"
                >
                  <Send className="w-3.5 h-3.5" />
                  {cooldownLeftSec(activeLink) > 0 ? `Resend in ${cooldownLeftSec(activeLink)}s` : 'Resend'}
                </button>
                <button
                  onClick={() => handleRefresh(activeLink)}
                  disabled={busyLinkId === activeLink.linkId}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-60 text-slate-700 text-xs font-semibold cursor-pointer"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${busyLinkId === activeLink.linkId ? 'animate-spin' : ''}`} />
                  Check status
                </button>
                <button
                  onClick={() => handleCancel(activeLink)}
                  disabled={busyLinkId === activeLink.linkId}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-rose-200 bg-rose-50 hover:bg-rose-100 disabled:opacity-60 text-rose-600 text-xs font-semibold cursor-pointer ml-auto"
                >
                  <Ban className="w-3.5 h-3.5" />
                  Cancel link
                </button>
              </div>
            </div>
          )}

          {/* Send form */}
          {!alreadyPaid && (
            <form onSubmit={handleSend} className="space-y-3.5" noValidate>
              <div>
                <label htmlFor="pl-amount" className="block text-xs font-semibold text-slate-600 mb-1">
                  Amount to collect (₹)
                </label>
                <input
                  id="pl-amount"
                  type="number"
                  min="1"
                  max="100000"
                  step="0.01"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={isClosedStage ? 'Cancellation charge (auto)' : 'Booking fare'}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                />
                {isClosedStage && (
                  <p className="text-[11px] text-slate-400 mt-1">
                    Leave blank to charge the booking’s cancellation / no-show fee.
                  </p>
                )}
              </div>

              <fieldset>
                <legend className="block text-xs font-semibold text-slate-600 mb-1.5">Send via</legend>
                <div className="flex flex-wrap gap-2">
                  {CHANNELS.map(({ key, label, icon: Icon }) => (
                    <label
                      key={key}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold cursor-pointer select-none transition-colors ${
                        channels[key]
                          ? 'bg-blue-50 border-blue-300 text-blue-700'
                          : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={channels[key]}
                        onChange={() => toggleChannel(key)}
                        className="sr-only"
                      />
                      <Icon className="w-3.5 h-3.5" />
                      {label}
                    </label>
                  ))}
                </div>
              </fieldset>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="pl-phone" className="block text-xs font-semibold text-slate-600 mb-1">
                    Patient phone
                  </label>
                  <input
                    id="pl-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+91 98765 43210"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="pl-email" className="block text-xs font-semibold text-slate-600 mb-1">
                    Patient email
                  </label>
                  <input
                    id="pl-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@example.com"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="pl-expiry" className="block text-xs font-semibold text-slate-600 mb-1">
                  Link valid for
                </label>
                <select
                  id="pl-expiry"
                  value={expiryMinutes}
                  onChange={(e) => setExpiryMinutes(e.target.value)}
                  disabled={!!activeLink && !amountChanged}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 disabled:bg-slate-50 disabled:text-slate-400"
                >
                  {EXPIRY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              {formError && (
                <p role="alert" className="text-xs font-medium text-rose-600">
                  {formError}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors cursor-pointer"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {submitting ? 'Sending…' : submitLabel}
              </button>
            </form>
          )}

          {/* History */}
          {pastLinks.length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Previous links</h3>
              <ul className="space-y-1.5">
                {pastLinks.map((l) => (
                  <li
                    key={l.linkId}
                    className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50/50 px-3 py-2 text-xs"
                  >
                    <span className="font-semibold text-slate-700">{formatRupees(l.amount)}</span>
                    <span className="text-slate-400">{formatDateTime(l.createdAt)}</span>
                    <StatusBadge status={l.status} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentLinkModal;
